import actionTypes, {
  FETCH_LATEST_POLL,
  FETCH_LATEST_POLL_SUCCESS,
  FETCH_LATEST_POLL_FAILURE,
} from './actionTypes';
import api, { createAuthenticatedApiInstance } from 'src/utils/api';
import { setAlert } from 'src/typescript/components/alert/actions';
import * as log from 'loglevel';
import postsFilterOptions from 'src/constants/postsFilterOptions';

export const fetchPosts = (communitySlug, filter = 'latest', topicId = null, topicName = '',
  isSeeMoreClick = false, year = null, month = null, pageNumber = null) => {
  const postsFilter = postsFilterOptions.find(option => option.value === filter) ? filter : 'latest';

  return async (dispatch, getState) => {
    const state = getState();
    const previousFilter = state.pages.communityPosts.filter;

    dispatch({
      type: actionTypes.FETCH_COMMUNITY_POSTS_PAGE_POSTS,
      archiveCurrentMonth: year ? month : null,
      archiveCurrentYear: year,
      filter: postsFilter,
      isSeeMoreClick,
      selectedTopic: topicName,
      selectedTopicId: topicId,
    });

    try {
      const currentUser = state.context.currentUser;
      const isMember = !!currentUser && !!currentUser.followedCommunities.find(x => x.slug === communitySlug);
      const api = createAuthenticatedApiInstance(getState);
      const url = `posts/${communitySlug}/${postsFilter}`;
      const createdBeforePostId = isSeeMoreClick && ['active', 'popular', 'pinned'].indexOf(filter) < 0
         ? state.pages.communityPosts.lastPostId
        : null;

      const currentPageNumber = state.pages.communityPosts.currentPageNumber;
      let newPageNumber = currentPageNumber;
      // pageNumber will be null each time fetchPosts() is called, except on archive page with page-number param in URL
      if (year) {
        // For archive (should be value of page-number param or 1 if param doesn't exist or is not a number)
        newPageNumber = pageNumber ? pageNumber : 1;
      } else if (!currentPageNumber || filter !== previousFilter) {
        // On page load or changing filter
        newPageNumber = 1;
      } else if (isSeeMoreClick) {
        newPageNumber = currentPageNumber + 1;
      }

      // pageNumber param only for archive page and for active, popular and pinned filters
      let pageNumberParam = null;
      if (year || isSeeMoreClick && ['active', 'popular', 'pinned'].indexOf(filter) >= 0) {
        pageNumberParam = newPageNumber;
      }

      // Params need to be null and not empty strings, or the API request URL will have them
      // and we get a 400 bad request error
      const params = {
        topicId,
        createdBeforePostId, // filters latest/unanswered
        year, // archive page
        month: year ? month : null, // archive page
        pageNumber: pageNumberParam, // archive page & filters active/popular/pinned
      };

      const { data: posts } = !isMember
        ? await api.getPublic(url, { params })
        : await api.get(`private/${url}`, { params });

      dispatch({
        type: actionTypes.FETCH_COMMUNITY_POSTS_PAGE_POSTS_SUCCESS,
        currentPageNumber: pageNumberParam !== null ? pageNumberParam : newPageNumber,
        hasMorePosts: posts.length < 30 ? false : true,
        isSeeMoreClick,
        posts,
      });
    } catch (ex) {
      log.error(ex);
      dispatch({ type: actionTypes.FETCH_COMMUNITY_POSTS_PAGE_POSTS_FAILURE, error: ex });
      dispatch(setAlert(ex.message));
    }
  };
};

export const fetchCommunityTopTags = (communityId, params) => {
  return async (dispatch) => {
    dispatch({ type: actionTypes.FETCH_COMMUNITY_TOP_TAGS });
    try {
      const { data } = await api.getPublic(`community-top-tags/${communityId}`, { params });

      dispatch({
        type: actionTypes.FETCH_COMMUNITY_TOP_TAGS_SUCCESS,
        topTags: data,
      });
    } catch (ex) {
      log.error(ex);
      dispatch({ type: actionTypes.FETCH_COMMUNITY_TOP_TAGS_FAILURE, error: ex });
      dispatch(setAlert(ex.message));
    }
  };
};

export const fetchLatestPoll = (communitySlug) => {
  return async (dispatch, getState) => {
    dispatch({ type: FETCH_LATEST_POLL });
    try {
      const state = getState();
      const currentUser = state.context.currentUser;
      const isMember = !!currentUser && !!currentUser.followedCommunities.find(x => x.slug === communitySlug);
      const api = createAuthenticatedApiInstance(getState);
      const url = `posts/${communitySlug}/latest-poll`;

      const { data: posts } = !isMember
        ? await api.getPublic(url)
        : await api.get(`private/${url}`);

      dispatch({
        type: FETCH_LATEST_POLL_SUCCESS,
        latestPoll: posts,
      });

    } catch (ex) {
      log.error(ex);
      dispatch({ type: FETCH_LATEST_POLL_FAILURE });
      dispatch(setAlert(ex.message));
    }
  };
};

export const fetchPinnedPosts = (communitySlug) => {
  return async (dispatch, getState) => {
    dispatch({ type: actionTypes.FETCH_POSTS_PINNED_POSTS });
    try {
      const state = getState();
      const currentUser = state.context.currentUser;
      const isMember = !!currentUser && !!currentUser.followedCommunities.find(x => x.slug === communitySlug);
      const api = createAuthenticatedApiInstance(getState);
      const url = `posts/${communitySlug}/pinned`;

      const { data } = !isMember
        ? await api.getPublic(url)
        : await api.get(`private/${url}`);

      const pinnedPosts = data.slice(0, 5).map(post => ({
        postId: post.postId,
        title: post.title,
        userImageHash: post.author.userImageHash,
        username: post.author.username,
      }));

      dispatch({
        type: actionTypes.FETCH_POSTS_PINNED_POSTS_SUCCESS,
        pinnedPosts,
      });
    } catch (ex) {
      log.error(ex);
      dispatch({ type: actionTypes.FETCH_POSTS_PINNED_POSTS_FAILURE, error: ex });
      dispatch(setAlert(ex.message));
    }
  };
};

export const fetchCommunityTopics = (communitySlug) => {
  return async (dispatch) => {
    dispatch({ type: actionTypes.FETCH_COMMUNITY_TOPICS });
    try {
      const { data } = await api.getPublic(`topics/${communitySlug}`);
      const topics = data.map(topic => ({
        id: topic.topicId,
        name: topic.name,
        postCount: topic.postCount,
      }));

      dispatch({
        type: actionTypes.FETCH_COMMUNITY_TOPICS_SUCCESS,
        topics,
      });
    } catch (ex) {
      dispatch({ type: actionTypes.FETCH_COMMUNITY_TOPICS_FAILURE, error: ex });
      dispatch(setAlert(ex.message));
    }
  };
};

export const fetchArchive = (communitySlug) => {
  return async (dispatch) => {
    dispatch({ type: actionTypes.FETCH_ARCHIVE });
    try {
      const { data } = await api.getPublic(`posts/${communitySlug}/archive`);

      dispatch({
        type: actionTypes.FETCH_ARCHIVE_SUCCESS,
        archiveData: data,
      });
    } catch (ex) {
      dispatch({ type: actionTypes.FETCH_ARCHIVE_FAILURE, error: ex });
      dispatch(setAlert(ex.message));
    }
  };
};

export const fetchTotalMonthlyPosts = (communitySlug, year, month) => {
  return async (dispatch, getState) => {
    dispatch({ type: actionTypes.FETCH_TOTAL_MONTHLY_POSTS });
    try {
      const state = getState();
      const currentUser = state.context.currentUser;
      const isMember = !!currentUser && !!currentUser.followedCommunities.find(x => x.slug === communitySlug);
      const api = createAuthenticatedApiInstance(getState);
      const url = `posts/${communitySlug}/total`;
      const params = { year, month };

      const { data: { totalPosts } } = !isMember
        ? await api.getPublic(url, { params })
        : await api.get(`private/${url}`, { params });

      dispatch({
        type: actionTypes.FETCH_TOTAL_MONTHLY_POSTS_SUCCESS,
        totalMonthlyPosts: totalPosts,
      });
    } catch (ex) {
      dispatch({ type: actionTypes.FETCH_TOTAL_MONTHLY_POSTS_FAILURE, error: ex });
      dispatch(setAlert(ex.message));
    }
  };
};
